import { default as cx } from "clsx";
import { html } from "htm/preact";
import { useCallback, useEffect, useMemo, useRef } from "preact/hooks";

import { type ContentLink as BibleContentLinkModel } from "../modules/bible";
import {
  PathCommand,
  Rect,
  Size,
  draw,
  textSelectionRects,
} from "../modules/canvas";
import { dispatchEvent } from "../modules/events";
import { invariant } from "../modules/helpers";
import { isLinkCollected } from "../modules/progress";

import { BibleContentItem } from "./BibleContentItem";
import { useThemeEffect } from "./ThemeProvider";

import { useBibleProgress, useBibleSettings } from "./BibleContent";

export const BibleContentLink = ({ link }: { link: BibleContentLinkModel }) => {
  const settings = useBibleSettings();
  if (settings.showLinks) {
    return html`<${Link} link=${link} />`;
  } else {
    return html`
      ${link.items.map(
        (item) => html`<${BibleContentItem} item=${item} key=${item.key} />`,
      )}
    `;
  }
};

const Link = ({ link }: { link: BibleContentLinkModel }) => {
  const ref = useRef<HTMLElement>(null);
  const resizeRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const background = useRef(useMemo(() => new BibleContentBackground(), []));
  const isCollected = isLinkCollected(useBibleProgress(), link.id);
  const onClick = useCallback(() => {
    dispatchEvent("linktapped", {
      link: link.payload,
    });
  }, []);
  /* c8 ignore start */
  useEffect(() => {
    const element = ref.current;
    const elementResize = resizeRef.current;
    const canvas = canvasRef.current;
    if (!element || !elementResize || !canvas) return;
    const observer = new ResizeObserver(() => {
      const rects = element.getClientRects();
      background.current.setup(canvas, Array.from(rects));
    });
    observer.observe(elementResize);
    return () => {
      observer.unobserve(elementResize);
    };
  }, []);
  useEffect(() => {
    background.current.collected = isCollected;
    background.current.draw();
  }, [isCollected]);
  useThemeEffect(() => {
    background.current.draw();
  });
  /* c8 ignore end */
  return html`
    <span
      class=${cx("BibleContentLink", {
        // HACK: In Chrome 66 (Android 9), ResizeObserver doesn't fire fire inline elements
        "BibleContentLink--fallback": /Chrome\/66/.test(navigator.userAgent),
        "BibleContentLink--collected": isCollected,
      })}
      data-testid="link"
      onClick=${onClick}
      ref=${ref}
    >
      <span class="BibleContentLink__resize" ref=${resizeRef}></span>
      <canvas class="BibleContentLink__background" ref=${canvasRef} />
      <span class="BibleContentLink__text">
        ${link.items.map(
          (item) => html`<${BibleContentItem} item=${item} key=${item.key} />`,
        )}
      </span>
    </span>
  `;
};

/* c8 ignore start */
class BibleContentBackground {
  private canvas: HTMLCanvasElement | null = null;

  collected = false;

  private commands: PathCommand[][] = [];

  private rects: Rect[] = [];

  constructor() {}

  draw() {
    const canvas = this.canvas;
    if (!canvas) return;
    const context = invariant(canvas.getContext("2d"));
    const colorFill = getComputedStyle(canvas).getPropertyValue(
      this.collected
        ? "--theme-link-collected-background-color"
        : "--theme-link-background-color",
    );
    const colorStroke = getComputedStyle(canvas).getPropertyValue(
      this.collected
        ? "--theme-link-collected-underline-color"
        : "--theme-link-underline-color",
    );
    context.save();
    context.clearRect(0, 0, canvas.width, canvas.height);
    // Background
    context.save();
    context.fillStyle = colorFill;
    for (const commands of this.commands) {
      draw(commands, context);
      context.fill();
    }
    context.restore();
    // Underline
    context.lineCap = "round";
    context.lineWidth = 2.5;
    context.setLineDash([3, 4]);
    context.strokeStyle = colorStroke;
    for (let rect of this.rects) {
      rect = rect.inset(0, 0);
      context.beginPath();
      context.moveTo(rect.bl.x, rect.bl.y);
      context.lineTo(rect.br.x, rect.br.y);
      context.stroke();
      context.closePath();
    }
    context.restore();
  }

  setup(canvas: HTMLCanvasElement, rects: DOMRect[]) {
    const inset = -4;
    const rect = Rect.fromDOMRect(
      invariant(canvas.parentElement).getBoundingClientRect(),
    ).inset(inset, inset);
    const context = invariant(canvas.getContext("2d"));
    const offsetX = rect.origin.x - rects[0].x;
    const offsetY = rect.origin.y - rects[0].y;
    const ratio = window.devicePixelRatio;
    canvas.height = rect.size.height * ratio;
    canvas.width = rect.size.width * ratio;
    canvas.style.height = `${rect.size.height}px`;
    canvas.style.width = `${rect.size.width}px`;
    canvas.style.transform = `translate(${offsetX}px,${offsetY}px)`;
    context.scale(ratio, ratio);
    this.canvas = canvas;
    this.rects = rects.map((r) =>
      Rect.fromDOMRect(r).offset(new Size(-rect.origin.x, -rect.origin.y)),
    );
    this.commands = textSelectionRects(
      this.rects.map((r) => r.inset(inset, inset)),
    );
    this.draw();
  }
}
/* c8 ignore end */
